import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getUserOrders } from '../services/userService';
import { getProductById } from '../services/productsService';
import { Package, Calendar, Truck, CheckCircle, XCircle, Clock } from 'phosphor-react';

function MyOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productImages, setProductImages] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch product images for order items
  const fetchProductImages = async (orders) => {
    const imageMap = {};
    const productIds = new Set();
    
    // Collect all unique product IDs from all orders
    orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          if (item.product_id) {
            productIds.add(item.product_id);
          }
        });
      }
    });

    // Fetch product data for each unique product ID
    const promises = Array.from(productIds).map(async (productId) => {
      try {
        const product = await getProductById(productId);
        if (product && product.image_url) {
          imageMap[productId] = product.image_url; // Use main image as thumbnail
        }
      } catch (err) {
        console.error(`Error fetching product ${productId}:`, err);
      }
    });

    await Promise.all(promises);
    setProductImages(imageMap);
  };

  // Fetch user orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const ordersData = await getUserOrders(user.id);
        setOrders(ordersData);
        
        // Fetch product images after orders are loaded
        if (ordersData.length > 0) {
          await fetchProductImages(ordersData);
        }
      } catch (err) {
        setError(t('common.error'));
        console.error('Error loading orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, t]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'processing':
        return 'text-blue-600 bg-blue-100';
      case 'shipped':
        return 'text-purple-600 bg-purple-100';
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} />;
      case 'processing':
        return <Package size={16} />;
      case 'shipped':
        return <Truck size={16} />;
      case 'delivered':
        return <CheckCircle size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('orders.pending');
      case 'processing':
        return t('orders.processing');
      case 'shipped':
        return t('orders.shipped');
      case 'delivered':
        return t('orders.delivered');
      case 'cancelled':
        return t('orders.cancelled');
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatOrderItems = (items) => {
    if (!items || !Array.isArray(items)) return t('orders.noItems');
    return items.map(item => `${item.name} (${item.quantity})`).join(', ');
  };

  const renderOrderItems = (items) => {
    if (!items || !Array.isArray(items)) {
      return <p className="text-sm text-gray-600">{t('orders.noItems')}</p>;
    }

    return (
      <div className="space-y-3">
        {items.map((item, index) => {
          const productImage = item.product_id ? productImages[item.product_id] : null;
          return (
            <Link 
              key={index} 
              to={`/product/${item.product_id}`}
              className="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="w-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden" style={{ aspectRatio: '105/148' }}>
                {productImage ? (
                  <img 
                    src={productImage} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package size={24} className="text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-base font-medium text-gray-900">{item.name}</p>
                <p className="text-sm text-gray-500">{item.quantity} × €{parseFloat(item.price).toFixed(2)}</p>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <Package size={64} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('orders.pleaseSignIn')}</h2>
            <p className="text-gray-600 mb-6">{t('orders.signInRequired')}</p>
            <Link to="/login" className="bg-ambre-600 text-white px-6 py-2 rounded-lg hover:bg-ambre-700 transition">
              {t('nav.login')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('orders.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle size={64} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('orders.errorLoading')}</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-ambre-600 text-white px-6 py-2 rounded-lg hover:bg-ambre-700 transition"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-16 px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('orders.title')}</h1>
            <p className="text-gray-600">{t('orders.subtitle')}</p>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <Package size={64} className="text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('orders.noOrders')}</h2>
              <p className="text-gray-600 mb-6">{t('orders.noOrdersSubtitle')}</p>
              <Link to="/shop" className="bg-ambre-600 text-white px-6 py-3 rounded-lg hover:bg-ambre-700 transition">
                {t('orders.startShopping')}
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-ambre-100 rounded-lg flex items-center justify-center">
                          <Package size={24} className="text-ambre-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('orders.orderNumber')}{order.order_number}</h3>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Calendar size={16} className="mr-2" />
                            {formatDate(order.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-4 md:mt-0">
                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          <span className="capitalize">{getStatusText(order.status)}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-ambre-600">
                            €{parseFloat(order.total_amount).toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-500">{order.currency || 'EUR'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">{t('orders.items')}</h4>
                          {renderOrderItems(order.items)}
                        </div>
                        <div className="text-right">
                          {/* <Link
                            to={`/order/${order.id}`}
                            className="inline-flex items-center text-ambre-600 hover:text-ambre-700 font-medium"
                          >
                            {t('orders.viewOrder')}
                            <span className="ml-1">→</span>
                          </Link> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default MyOrders; 